import type { PropertyDef, PropertyValue, Row, SelectOption } from "@bundle/contracts";
import { Db } from "@bundle/core";
import { useState } from "react";
import { Checkbox, Popover, Pressable, Stack, Text, TextField } from "../../primitives/index.ts";
import { useT } from "../../i18n/I18nProvider.tsx";
import { CellValue } from "./CellValue.tsx";
import { DateEditor } from "./DateEditor.tsx";
import type { CellContext } from "./labels.ts";
import { OptionPicker } from "./OptionPicker.tsx";
import { PeoplePicker } from "./PeoplePicker.tsx";
import { RelationPicker } from "./RelationPicker.tsx";
import { ContactPicker } from "../../mail/ContactCells.tsx";

export type PropertyCellProps = {
  prop: PropertyDef;
  row: Row;
  ctx: CellContext;
  /** "table": dense cell; "panel": a row of the properties panel (shows "Empty"). */
  variant: "table" | "panel";
  onChange: (value: PropertyValue | null) => void;
  onCreateOption: (name: string) => SelectOption | undefined;
  testID: string;
};

/** Text the inline text editor starts from. */
function editText(prop: PropertyDef, row: Row): string {
  const v = Db.cellValue(row, prop);
  if (v === undefined) return "";
  if (v.type === "number") return String(v.number);
  return Db.cellText(row, prop);
}

/** The value for edited text (null clears; invalid numbers are ignored → undefined). */
function textValue(prop: PropertyDef, text: string): PropertyValue | null | undefined {
  const trimmed = text.trim();
  switch (prop.type) {
    case "title":
      return { type: "title", text: trimmed };
    case "text":
      return trimmed === "" ? null : { type: "text", text };
    case "url":
      return trimmed === "" ? null : { type: "url", url: trimmed };
    case "email":
      return trimmed === "" ? null : { type: "email", email: trimmed };
    case "number": {
      if (trimmed === "") return null;
      const n = Number(trimmed.replace(",", "."));
      return Number.isNaN(n) ? undefined : { type: "number", number: n };
    }
    default:
      return undefined;
  }
}

function isTextLike(prop: PropertyDef): boolean {
  return prop.type === "title" || prop.type === "text" || prop.type === "url" || prop.type === "email" || prop.type === "number";
}

/** One editable cell: shows the value; a press opens the editor for its type (popover), checkboxes toggle in place. */
export function PropertyCell({ prop, row, ctx, variant, onChange, onCreateOption, testID }: PropertyCellProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const display = Db.displayValue(prop, row, ctx.tzOffsetMinutes);
  const readOnly = ctx.readOnly || Db.isReadOnlyType(prop.type);
  const pad = variant === "table" ? "2" : "1.5";

  if (prop.type === "checkbox") {
    return (
      <Stack direction="horizontal" align="center" paddingX={pad} paddingY="1" testID={testID}>
        <Checkbox
          value={display.kind === "checkbox" && display.checked}
          onChange={(v) => (readOnly ? undefined : onChange({ type: "checkbox", checked: v }))}
          disabled={readOnly}
          accessibilityLabel={prop.name}
          testID={`${testID}-checkbox`}
        />
      </Stack>
    );
  }

  const value = <CellValue display={display} people={ctx.people} titles={ctx.titles} showEmpty={variant === "panel"} testID={`${testID}-value`} />;
  if (readOnly) {
    return (
      <Stack direction="horizontal" align="center" paddingX={pad} paddingY="1.5" testID={testID}>
        {value}
      </Stack>
    );
  }

  const commitText = () => {
    const v = textValue(prop, draft);
    if (v !== undefined && draft !== editText(prop, row)) onChange(v);
  };
  const close = () => {
    if (isTextLike(prop)) commitText();
    setOpen(false);
  };
  const ids = Db.cellIds(row, prop);
  const current = Db.cellValue(row, prop);

  const editor = () => {
    if (isTextLike(prop)) {
      return (
        <Stack padding="2" testID={`${testID}-editor`}>
          <TextField
            value={draft}
            onChange={setDraft}
            autoFocus
            multiline={prop.type === "text" || prop.type === "title"}
            keyboard={prop.type === "number" ? "number" : prop.type === "url" ? "url" : prop.type === "email" ? "email" : "default"}
            onSubmit={close}
            onCancel={() => setOpen(false)}
            testID={`${testID}-input`}
          />
        </Stack>
      );
    }
    switch (prop.type) {
      case "select":
      case "status":
      case "multi_select":
        return (
          <OptionPicker
            prop={prop}
            selected={ids}
            multiple={prop.type === "multi_select"}
            onChange={(next) => {
              if (prop.type === "multi_select") onChange(next.length === 0 ? null : { type: "multi_select", optionIds: next });
              else if (next[0] === undefined) onChange(null);
              else onChange(prop.type === "status" ? { type: "status", optionId: next[0] } : { type: "select", optionId: next[0] });
            }}
            onCreate={onCreateOption}
            onDone={() => setOpen(false)}
            testID={`${testID}-options`}
          />
        );
      case "person":
        return (
          <PeoplePicker
            people={ctx.people}
            selected={ids}
            onChange={(next) => onChange(next.length === 0 ? null : { type: "person", userIds: next })}
            onDone={() => setOpen(false)}
            testID={`${testID}-people`}
          />
        );
      case "relation":
        return (
          <RelationPicker
            options={ctx.relationOptions[prop.id] ?? []}
            selected={ids}
            onChange={(next) => onChange(next.length === 0 ? null : { type: "relation", pageIds: next })}
            onDone={() => setOpen(false)}
            testID={`${testID}-relation`}
          />
        );
      case "contact":
        return (
          <ContactPicker
            selected={ids}
            onChange={(next) => onChange(next.length === 0 ? null : { type: "contact", contactIds: next })}
            onDone={() => setOpen(false)}
            testID={`${testID}-contacts`}
          />
        );
      case "date":
        return (
          <DateEditor
            value={current?.type === "date" ? current.date : undefined}
            weekStartsOn={ctx.weekStartsOn}
            onChange={(d) => onChange(d === undefined ? null : { type: "date", date: d })}
            testID={`${testID}-date`}
          />
        );
      default:
        return (
          <Stack padding="3" testID={`${testID}-files`}>
            <Text variant="footnote" color="text.secondary">
              {t("db.files.comingSoon")}
            </Text>
          </Stack>
        );
    }
  };

  return (
    <Popover
      open={open}
      onClose={close}
      side="bottom"
      align="start"
      fill
      width={prop.type === "date" ? undefined : "picker"}
      anchor={
        <Pressable
          direction="horizontal"
          radius={variant === "table" ? "none" : "sm"}
          onPress={() => {
            setDraft(editText(prop, row));
            setOpen(true);
          }}
          testID={testID}
        >
          <Stack grow direction="horizontal" align="center" paddingX={pad} paddingY="1.5">
            {value}
          </Stack>
        </Pressable>
      }
    >
      <Stack testID={`${testID}-popover`}>{open ? editor() : null}</Stack>
    </Popover>
  );
}
